import { Injectable, inject } from '@angular/core';
import { Observable, map, of, tap, switchMap } from 'rxjs';
import { ProductService } from '@core';
import type { ProductDetails, ProductDetailsWithVariant, ProductVariance } from '@models';
import type { RadioEngravingOption } from '../../../../ui/apple-components/radio-engraving/radio-engraving-button.component';
import { EngravingUtilitiesService } from './engraving-utilities.service';

export interface EngravingData {
  hasEngraving: boolean;
  engravingOptions: RadioEngravingOption[];
  engravingVariance: any | null;
}

export interface EngravingState {
  firstLine: string;
  secondLine: string;
}

// Cache interface for Engraving data
interface CachedEngravingData {
  data: EngravingData;
  timestamp: number;
}

@Injectable({
  providedIn: 'root',
})
export class ProductEngravingService extends EngravingUtilitiesService {
  private productService = inject(ProductService);

  // Engraving data cache with 10-minute timeout
  private engravingCache = new Map<string, CachedEngravingData>();
  private readonly CACHE_TIMEOUT_MS = 10 * 60 * 1000; // 10 minutes

  /**
   * Cache utility methods
   */
  private isCacheValid(cachedItem: CachedEngravingData): boolean {
    return Date.now() - cachedItem.timestamp < this.CACHE_TIMEOUT_MS;
  }

  private getCachedEngraving(productId: string): EngravingData | null {
    const cached = this.engravingCache.get(productId);
    if (cached && this.isCacheValid(cached)) {
      return cached.data;
    } else if (cached) {
      this.engravingCache.delete(productId);
    }
    return null;
  }

  private setCachedEngraving(productId: string, data: EngravingData): void {
    const cachedData: CachedEngravingData = {
      data,
      timestamp: Date.now(),
    };
    this.engravingCache.set(productId, cachedData);
  }

  /**
   * Clear expired cache entries
   */
  clearExpiredEngravingCache(): void {
    const now = Date.now();
    let removedCount = 0;

    for (const [productId, cached] of this.engravingCache.entries()) {
      if (!this.isCacheValid(cached)) {
        this.engravingCache.delete(productId);
        removedCount++;
      }
    }
  }

  /**
   * Get cache statistics
   */
  getEngravingCacheStats(): { cacheSize: number; validEntries: number } {
    let validEntries = 0;

    for (const cached of this.engravingCache.values()) {
      if (this.isCacheValid(cached)) {
        validEntries++;
      }
    }

    return {
      cacheSize: this.engravingCache.size,
      validEntries,
    };
  }

  /**
   * Check if a product supports engraving and get engraving options
   * Uses cache first, API as fallback
   */
  checkForEngraving(productId: string): Observable<EngravingData> {
    // Check cache first
    const cached = this.getCachedEngraving(productId);
    if (cached) {
      return of(cached);
    }

    // Try to get from ProductService cache first
    const productCacheData = this.productService.getCachedProductData(productId);
    if (productCacheData) {
      return this.processEngravingFromCachedData(productId, productCacheData.productVariances);
    }

    // Fallback to API call through ProductService (which will cache the result)
    return this.productService
      .getProductVariances(productId)
      .pipe(switchMap((variances) => this.processEngravingFromCachedData(productId, variances)));
  }

  /**
   * Process engraving data from cached product variances
   */
  private processEngravingFromCachedData(productId: string, variances: any[]): Observable<EngravingData> {
    const engravingVariance = variances.find((v) => v.controlType === 'Text' && v.options?.length);

    const options: RadioEngravingOption[] = [];

    if (engravingVariance) {
      const firstOption = engravingVariance.options[0];
      const engravingOption: RadioEngravingOption = {
        id: firstOption.id,
        name: 'Add engraving',
        description: "It won't impact your warranty with Apple",
        value: 'Add engraving',
        price: 'Free',
      };
      options.push(engravingOption);
    }

    // Always include a "No Engraving" fallback
    options.push({
      id: 'no-engraving',
      name: 'No engraving',
      value: 'No engraving',
    });

    const result: EngravingData = {
      hasEngraving: !!engravingVariance,
      engravingOptions: options,
      engravingVariance: engravingVariance || null,
    };

    // Cache the result
    this.setCachedEngraving(productId, result);

    return of(result);
  }

  /**
   * Process engraving selection change
   */
  processEngravingSelection(event: any): {
    selectedEngravingOption: boolean;
    selectedEngravingValue: string;
    shouldOpenModal: boolean;
    shouldClearEngraving: boolean;
  } {
    if (event.name === 'Add engraving') {
      return {
        selectedEngravingOption: true,
        selectedEngravingValue: event.id,
        shouldOpenModal: true,
        shouldClearEngraving: false,
      };
    } else {
      return {
        selectedEngravingOption: false,
        selectedEngravingValue: event.id,
        shouldOpenModal: false,
        shouldClearEngraving: true,
      };
    }
  }

  /**
   * Check if engraving text exists
   */
  hasEngravingText(engraving: EngravingState): boolean {
    return !!(engraving.firstLine.trim() || engraving.secondLine.trim());
  }

  /**
   * Parse engraving result from modal service
   */
  parseEngravingResult(engravingText: string): EngravingState {
    const lines = engravingText.split('\n');
    return {
      firstLine: lines[0] || '',
      secondLine: lines[1] || '',
    };
  }

  /**
   * Create variant selection for cart with engraving
   */
  createEngravingVariantSelection(
    product: ProductDetailsWithVariant,
    engraving: EngravingState,
    engravingVariance: any,
  ): any | null {
    if (!this.hasEngravingText(engraving)) {
      return null;
    }

    const matchingGrouping = product.variantGroupings?.find((g) => g.parentProductId === product.productId);

    if (!engravingVariance || !matchingGrouping) {
      return null;
    }

    const engravingText = [engraving.firstLine, engraving.secondLine]
      .filter((line) => line && line.trim() !== '')
      .join('\n');

    if (!engravingText) {
      return null;
    }

    return {
      productVariantGroupingsId: matchingGrouping.id,
      name: 'Engraving',
      options: [
        {
          productVarianceId: engravingVariance.id,
          productVarianceOptionId: null,
          name: engravingVariance.name || 'Engraving',
          controlType: engravingVariance.controlType || 'Text',
          selectedValue: engravingText,
        },
      ],
    };
  }

  /**
   * Format engraving text for display
   */
  formatEngravingForDisplay(engraving: EngravingState): string {
    return [engraving.firstLine, engraving.secondLine].filter(Boolean).join('\n');
  }

  /**
   * Validate engraving text against product constraints
   */
  validateEngravingText(
    engraving: EngravingState,
    maxLines: number = 2,
    maxCharactersPerLine: number = 50,
  ): {
    isValid: boolean;
    errors: string[];
  } {
    const errors: string[] = [];
    const lines = [engraving.firstLine, engraving.secondLine].filter(Boolean);

    if (lines.length > maxLines) {
      errors.push(`Maximum ${maxLines} lines allowed`);
    }

    for (const line of lines) {
      if (line.length > maxCharactersPerLine) {
        errors.push(`Maximum ${maxCharactersPerLine} characters per line`);
      }
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  }

  /**
   * Clear all caches
   */
  clearAllCaches(): void {
    const count = this.engravingCache.size;
    this.engravingCache.clear();
  }

  /**
   * Batch clear expired caches from all services (utility method)
   */
  performCacheCleanup(): void {
    this.clearExpiredEngravingCache();
  }
}
